import Image from "next/image";

import geminiLogo from "@/components/icons/ai/Gemini.png";
import { Icon } from "@/components/icons/Icon";

export const CHAT_MODEL_META_KEY = "chatModel";

export type ChatModelProvider = "anthropic" | "google" | "openai";

type ChatModelOptionConfig = {
  id: string;
  label: string;
  description?: string;
  provider?: ChatModelProvider;
  icon: React.ReactNode;
};

// Keep selectable model ids in sync with apps/chat-bot/src/openai/chatModels.ts.
// Auto resolves to the backend default; backend-only models stay out of this picker.
export const CHAT_MODEL_OPTIONS = [
  {
    id: "auto",
    label: "Auto",
    description: "Best for all uses",
    icon: <Icon name="ai/Corca" />,
  },
  {
    id: "gpt-6.1-sol",
    label: "GPT 6.1 Sol",
    description: "Most capable",
    provider: "openai",
    icon: <Icon name="ai/Chatgpt" />,
  },
  {
    id: "gpt-6.1-terra",
    label: "GPT 6.1 Terra",
    description: "Fast and capable",
    provider: "openai",
    icon: <Icon name="ai/Chatgpt" />,
  },
  {
    id: "claude-opus-5-5",
    label: "Opus 5.5",
    description: "For coding tasks",
    provider: "anthropic",
    icon: <Icon name="ai/Claude" />,
  },
  {
    id: "claude-sonnet-5-5",
    label: "Sonnet 5.5",
    description: "Fastest",
    provider: "anthropic",
    icon: <Icon name="ai/Sonnet" />,
  },
  {
    id: "gemini-3.8-flash",
    label: "Gemini 3.8 Flash",
    description: "Best for visual tasks",
    provider: "google",
    icon: <Image src={geminiLogo} alt="Gemini Logo" width={24} height={24} />,
  },
] as const satisfies readonly ChatModelOptionConfig[];

export type ChatModelId = (typeof CHAT_MODEL_OPTIONS)[number]["id"];
export type ChatModelOption = ChatModelOptionConfig & { id: ChatModelId };

export const DEFAULT_CHAT_MODEL_ID = "auto" satisfies ChatModelId;

function findChatModelOption(id: string): ChatModelOption | undefined {
  return CHAT_MODEL_OPTIONS.find((option) => option.id === id);
}

function getRequiredDefaultChatModelOption(): ChatModelOption {
  const defaultOption = findChatModelOption(DEFAULT_CHAT_MODEL_ID);
  if (!defaultOption) {
    throw new Error(`Default chat model is missing: ${DEFAULT_CHAT_MODEL_ID}`);
  }

  return defaultOption;
}

const DEFAULT_CHAT_MODEL_OPTION = getRequiredDefaultChatModelOption();

export function getChatModelOption(id?: string): ChatModelOption {
  return (id ? findChatModelOption(id) : undefined) ?? DEFAULT_CHAT_MODEL_OPTION;
}
